import { useCallback, useEffect, useMemo } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { POST, PUT } from '@/api/base';
import { ENDPOINTS } from '@/api/endpoints';
import { useCountriesOptions } from '@/hooks/api/use-countries-options';
import { useGetClinicById } from '@/hooks/api/use-get-clinic-by-id';
import { toast_success } from '@/libs/toast-wrappers';

import { Hr } from '@/components/ui/divider';
import { InputField } from '@/components/form/input-field';
import { SelectField } from '@/components/form/select-field';
import { Button } from '@/components/ui/button';

const createSchema = (t: TFunction) =>
  z.object({
    clinic_name: z.string().trim().min(1, t('clinic-name-error')),
    description: z.string().trim(),
    contact_url: z.string().trim().min(1, t('contact-url-error')),
    contact_phone: z.string().trim().min(1, t('contact-phone-error')),
    fax_number: z
      .string()
      .trim()
      .refine(
        (val) => val === '' || /^\+\d{1,3}[-.\s]?\d{1,4}[-.\s]?\d{1,10}$/.test(val),
        t('fax-number-format-error')
      ),
    street_address: z.string().trim().min(1, t('street-address-error')),
    city: z.string().trim().min(1, t('city-error')),
    province_or_state: z.string().trim().min(1, t('province-or-state-error')),
    postal_code: z.string().trim().min(1, t('postal-code-error')),
    country: z.string().trim().min(1, t('country-error')),
  });

type InputTypes = z.infer<ReturnType<typeof createSchema>>;

export function ClinicForm() {
  const { COUNTRIES } = useCountriesOptions();
  const { clinicId } = useParams();
  const { clinic, loading } = useGetClinicById({ clinic_id: clinicId ?? '' });
  const isEditing = Boolean(clinicId);
  const { onFormClose } = useOutletContext<{
    onFormClose: () => Promise<void>;
  }>();
  const { t } = useTranslation();

  const schema = useMemo(() => createSchema(t), [t]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<InputTypes>({ resolver: zodResolver(schema) });

  const onSubmitUpdateClinic: SubmitHandler<InputTypes> = async (data) => {
    if (isEditing && clinicId) {
      const response = await PUT(`${ENDPOINTS.CLINIC}`, data, {
        parameters: { clinicId },
      });

      if (response) {
        await onFormClose();
        toast_success(t('clinic-updated').replace('clinicId', clinicId.slice(0, 5)));
        return;
      }
    }
    const response = await POST(ENDPOINTS.CLINICS, data, { query: { add_site: 'True' } });

    if (response) {
      await onFormClose();
      toast_success(t('clinic-saved'));
    }
  };

  const fillForm = useCallback(() => {
    setValue('clinic_name', clinic?.clinic_name ?? '');
    setValue('description', clinic?.description ?? '');
    setValue('contact_phone', clinic?.contact_phone ?? '');
    setValue('contact_url', clinic?.contact_url ?? '');
    setValue('fax_number', clinic?.fax_number ?? '');
    setValue('country', clinic?.country ?? '');
    setValue('street_address', clinic?.street_address ?? '');
    setValue('province_or_state', clinic?.province_or_state ?? '');
    setValue('city', clinic?.city ?? '');
    setValue('postal_code', clinic?.postal_code ?? '');
  }, [clinic, setValue]);

  useEffect(() => {
    fillForm();
  }, [fillForm, clinic]);

  return (
    <form onSubmit={handleSubmit(onSubmitUpdateClinic)}>
      <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2 mb-8">
        <div className="sm:col-span-3">
          <InputField
            disabled={loading}
            {...register('clinic_name')}
            label={t('clinic-name-label')}
            errorMessage={errors.clinic_name?.message}
          />
        </div>
        <div className="sm:col-span-3">
          <InputField
            disabled={loading}
            {...register('description')}
            label={t('description-label')}
          />
        </div>

        <div className="col-span-full">
          <InputField
            disabled={loading}
            {...register('contact_url')}
            label={t('url-label')}
            errorMessage={errors.contact_url?.message}
          />
        </div>

        <div className="sm:col-span-3">
          <InputField
            disabled={loading}
            {...register('contact_phone')}
            label={t('phone-label')}
            errorMessage={errors.contact_phone?.message}
            type="phone"
          />
        </div>

        <div className="col-span-3">
          <InputField
            disabled={loading}
            {...register('fax_number')}
            label={t('fax-number-label')}
            placeholder="+1-212-9876543 or +999-9999-0"
            errorMessage={errors.fax_number?.message}
          />
        </div>

        <div className="sm:col-span-full">
          <SelectField
            disabled={loading}
            {...register('country')}
            label={t('country-label')}
            errorMessage={errors.country?.message}
            options={COUNTRIES ?? []}
          />
        </div>

        <div className="col-span-full">
          <InputField
            disabled={loading}
            {...register('street_address')}
            label={t('address-label')}
            errorMessage={errors.street_address?.message}
          />
        </div>

        <div className="sm:col-span-2 sm:col-start-1">
          <InputField
            disabled={loading}
            {...register('city')}
            label={t('city-label')}
            errorMessage={errors.city?.message}
          />
        </div>

        <div className="sm:col-span-2">
          <InputField
            disabled={loading}
            {...register('province_or_state')}
            label={t('province-or-state-label')}
            errorMessage={errors.province_or_state?.message}
          />
        </div>

        <div className="sm:col-span-2">
          <InputField
            disabled={loading}
            {...register('postal_code')}
            label={t('postal-code-label')}
            type="number"
            errorMessage={errors.postal_code?.message}
          />
        </div>
      </div>
      <Hr />
      <div className="mt-6 flex items-center justify-end gap-x-2">
        <Button onClick={onFormClose} variant="ghost-muted">
          {t('cancel')}
        </Button>
        <Button type="submit">{t('save')}</Button>
      </div>
    </form>
  );
}
